// Times in the conversation: a divider before a message when 30 minutes or a new day passed since the
// one before, and each message's exact time with its timezone on hover or tap. In the browser's
// timezone and locale; tests pass their own.

export interface Zone {
  locale?: string;
  timeZone?: string;
}

/** The gap after which a message gets a divider of its own. */
export const DIVIDER_GAP_MS = 30 * 60_000;

/** The calendar day in the zone, as a day number, and its year. */
function day(at: number, zone: Zone): { n: number; year: number } {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone.timeZone, year: "numeric", month: "numeric", day: "numeric" }).formatToParts(at);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { n: Date.UTC(part("year"), part("month") - 1, part("day")) / 86_400_000, year: part("year") };
}

/** "3:52 PM", or the locale's own form. */
export function clockTime(at: number, zone: Zone = {}): string {
  return new Date(at).toLocaleTimeString(zone.locale, { timeZone: zone.timeZone, hour: "numeric", minute: "2-digit" });
}

/** A divider's label: "Today 3:52 PM", "Yesterday 9:10 AM", "Oct 26, 9:10 AM", with the year when it is not this year's. */
export function dividerLabel(at: number, now: number, zone: Zone = {}): string {
  const then = day(at, zone);
  const today = day(now, zone);
  const time = clockTime(at, zone);
  if (today.n - then.n <= 0) return `Today ${time}`;
  if (today.n - then.n === 1) return `Yesterday ${time}`;
  const date = new Date(at).toLocaleDateString(zone.locale, {
    timeZone: zone.timeZone,
    month: "short",
    day: "numeric",
    ...(then.year === today.year ? {} : { year: "numeric" }),
  });
  return `${date}, ${time}`;
}

/** The exact time with the timezone's abbreviation: "Oct 28, 2026, 3:52 PM GMT+2". */
export function exactTime(at: number, zone: Zone = {}): string {
  return new Date(at).toLocaleString(zone.locale, {
    timeZone: zone.timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/** Whether a message at `at` gets a divider: the first with a time, or 30 minutes or a new day after `previous`. */
export function needsDivider(previous: number | undefined, at: number, zone: Zone = {}): boolean {
  return previous === undefined || at - previous >= DIVIDER_GAP_MS || day(at, zone).n !== day(previous, zone).n;
}
